import { pauseParallax, resumeParallax } from '../utils/parallax';

// How many pixels of dialog scroll it takes to fully fade the image out.
const DIALOG_IMAGE_FADE_DISTANCE = 300;

/**
 * About Me Dialog js logic.
 */
class AboutMeDialog {
  constructor() {
    this.dialogs = new Map();
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.animationDuration = 400;
    this.scrollPosition = 0;
    this.siteHeader = document.querySelector('.header');
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleClick.bind(this), true);
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.initializeDialogs();
  }

  initializeDialogs() {
    const dialogs = document.querySelectorAll('.about-me__dialog');
    dialogs.forEach(dialog => {
      const dialogId = dialog.id;
      this.dialogs.set(dialogId, {
        element: dialog,
        trigger: document.querySelector(`[data-dialog-trigger="${dialogId}"]`),
        closeButton: dialog.querySelector(`[data-dialog-close="${dialogId}"]`),
        backdrop: dialog.querySelector(`[data-dialog-backdrop="${dialogId}"]`),
        container: dialog.querySelector('.about-me__dialog-container'),
        isOpen: false,
        previousFocus: null,
        isAnimating: false,
        scrollHandler: null,
      });
    });
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-dialog-trigger]');
    const closeButton = event.target.closest('[data-dialog-close]');
    const backdrop = event.target.closest('[data-dialog-backdrop]');
    const dialogContainer = event.target.closest('.about-me__dialog-container');

    if (trigger) {
      if (trigger.tagName.toLowerCase() === 'a' || trigger.href) {
        event.preventDefault();
      }
      event.stopPropagation();
      this.openDialog(trigger.dataset.dialogTrigger);
    } else if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      if (closeButton.dataset.dialogClose) this.closeDialog(closeButton.dataset.dialogClose);
    } else if (backdrop && !dialogContainer) {
      event.preventDefault();
      event.stopPropagation();
      if (backdrop.dataset.dialogBackdrop) this.closeDialog(backdrop.dataset.dialogBackdrop);
    }

    const dialog = event.target.closest('.about-me__dialog');
    if (dialog && event.target === dialog) {
      event.preventDefault();
      event.stopPropagation();
      if (dialog.id && this.dialogs.get(dialog.id)?.isOpen) {
        this.closeDialog(dialog.id);
      }
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.dialogs.forEach((dialogData, dialogId) => {
        if (dialogData.isOpen && !dialogData.isAnimating) {
          this.closeDialog(dialogId);
        }
      });
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === 'Tab') {
      this.dialogs.forEach((dialogData) => {
        if (dialogData.isOpen) this.trapFocus(event, dialogData.element);
      });
    }
  }

  /**
   * Update opacity on the <img> inside .about-me__dialog-image based on
   * how far the dialog has been scrolled. Fades to 0 over
   * DIALOG_IMAGE_FADE_DISTANCE px, restores as the user scrolls back up.
   */
  updateDialogImageOpacity(dialogElement, scrollTop) {
    const img = dialogElement.querySelector('.about-me__dialog-image img');
    if (!img) return;
    img.style.opacity = Math.max(0, 1 - scrollTop / DIALOG_IMAGE_FADE_DISTANCE);
  }

  openDialog(dialogId) {
    const dialogData = this.dialogs.get(dialogId);
    if (!dialogData || dialogData.isOpen || dialogData.isAnimating) return;

    const { element, trigger, backdrop } = dialogData;

    this.scrollPosition = window.scrollY;
    dialogData.isAnimating = true;
    dialogData.previousFocus = document.activeElement;

    pauseParallax();

    if (backdrop) {
      if (backdrop.parentElement !== document.body) {
        document.body.appendChild(backdrop);
      }
      backdrop.classList.remove('about-me__dialog-backdrop--closing');
    }

    if (this.siteHeader) {
      this.siteHeader.classList.add('header--dialog-open');
    }

    this.preventBodyScroll(true);

    element.classList.add('about-me__dialog--hidden');
    element.showModal();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.classList.remove('about-me__dialog--hidden');
        element.classList.add('about-me__dialog--open');

        if (backdrop) backdrop.classList.add('about-me__dialog-backdrop--open');

        dialogData.isOpen = true;
        trigger.setAttribute('aria-expanded', 'true');

        // Ensure img is fully visible when dialog opens.
        this.updateDialogImageOpacity(element, 0);

        setTimeout(() => this.setInitialFocus(element), 100);
      });
    });

    setTimeout(() => {
      dialogData.isAnimating = false;

      // capture:true catches scroll from whichever child actually scrolls.
      // event.target is that child, so scrollTop is always correct.
      dialogData.scrollHandler = (event) => {
        this.updateDialogImageOpacity(element, event.target.scrollTop);
      };

      element.addEventListener('scroll', dialogData.scrollHandler, {
        passive: true,
        capture: true,
      });
    }, this.animationDuration);

    element.dispatchEvent(new CustomEvent('aboutme:dialog:opened', {
      detail: { dialogId },
      bubbles: true,
    }));
  }

  closeDialog(dialogId) {
    const dialogData = this.dialogs.get(dialogId);
    if (!dialogData || !dialogData.isOpen || dialogData.isAnimating) return;

    const { element, trigger, backdrop } = dialogData;

    dialogData.isAnimating = true;
    element.classList.add('about-me__dialog--closing');

    if (backdrop) {
      backdrop.classList.remove('about-me__dialog-backdrop--open');
      backdrop.classList.add('about-me__dialog-backdrop--closing');
    }

    trigger.setAttribute('aria-expanded', 'false');

    if (dialogData.scrollHandler) {
      element.removeEventListener('scroll', dialogData.scrollHandler, { capture: true });
      dialogData.scrollHandler = null;
    }

    setTimeout(() => {
      dialogData.isOpen = false;
      dialogData.isAnimating = false;

      element.close();
      element.classList.remove('about-me__dialog--open', 'about-me__dialog--closing');

      // Reset img opacity for the next open.
      this.updateDialogImageOpacity(element, 0);

      if (backdrop) backdrop.classList.remove('about-me__dialog-backdrop--closing');

      this.restoreBodyScroll();

      if (this.siteHeader) {
        this.siteHeader.classList.remove('header--dialog-open');
      }

      setTimeout(() => resumeParallax(), 50);

      element.dispatchEvent(new CustomEvent('aboutme:dialog:closed', {
        detail: { dialogId },
        bubbles: true,
      }));
    }, this.animationDuration);
  }

  setInitialFocus(dialog) {
    const el = dialog.querySelector('.about-me__dialog-close') || dialog.querySelector(this.focusableElements);
    if (el) el.focus();
  }

  trapFocus(event, dialog) {
    const focusable = Array.from(dialog.querySelectorAll(this.focusableElements))
      .filter(el => !el.disabled && el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  preventBodyScroll(prevent) {
    if (prevent) {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
    }
  }

  restoreBodyScroll() {
    const scrollY = Math.max(0, this.scrollPosition || 0);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflowY = '';

    requestAnimationFrame(() => {
      if (scrollY > 0) window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    });
  }

  open(dialogId) { this.openDialog(dialogId); }
  close(dialogId) { this.closeDialog(dialogId); }
  isOpen(dialogId) {
    return this.dialogs.get(dialogId)?.isOpen ?? false;
  }

  destroy() {
    this.dialogs.forEach((dialogData) => {
      if (dialogData.scrollHandler && dialogData.element) {
        dialogData.element.removeEventListener('scroll', dialogData.scrollHandler, { capture: true });
      }
    });
    document.removeEventListener('click', this.handleClick, true);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.dialogs.clear();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.aboutMeDialog = new AboutMeDialog();
  });
} else {
  window.aboutMeDialog = new AboutMeDialog();
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = AboutMeDialog;
}
